/**
 * PINNED ITEM WITH STATUS COMPONENT
 * 
 * Shows a pinned item in conversation with status.
 * Features:
 * - Click to view item details (via callback to parent)
 * - Sellers can change the status
 * - Either participant can remove the item from conversation
 * - Shows "Suggested by seller" label when seller added the item
 */

import React, { useState, useRef, useEffect } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { updateItemStatus, removeItemFromConversation } from '../../messaging/messagingService';
import type { AttachedItem } from '../../messaging/types';
import { ChevronDown, Loader2, X, ExternalLink, Sparkles } from 'lucide-react';
import { AppIcons } from '../../constants/icons';

interface PinnedItemWithStatusProps {
  item: AttachedItem;
  conversationId: string;
  currentUserId: string;
  isOwner: boolean; // Is the current user the owner of this item?
  onViewDetails?: (item: AttachedItem) => void; // Callback when user wants to view item details
}

const PinnedItemWithStatus: React.FC<PinnedItemWithStatusProps> = ({
  item,
  conversationId,
  currentUserId,
  isOwner,
  onViewDetails
}) => {
  const [isChanging, setIsChanging] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const currentStatus = item.status || 'available';

  const statusOptions = [
    { value: 'available', label: 'Available', icon: AppIcons.Available, color: 'bg-green-500', iconColor: 'text-green-500' },
    { value: 'pending', label: 'Pending', icon: AppIcons.Pending, color: 'bg-yellow-500', iconColor: 'text-yellow-500' },
    { value: 'sold', label: 'Sold', icon: AppIcons.Sold, color: 'bg-red-500', iconColor: 'text-red-500' },
  ];

  const currentStatusInfo = statusOptions.find(s => s.value === currentStatus) || statusOptions[0];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  // Handle clicking to view item details
  const handleItemClick = () => {
    if (onViewDetails) {
      onViewDetails(item);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (newStatus === currentStatus || isChanging) return;
    
    setIsChanging(true);
    setShowDropdown(false);

    try {
      // 1. Update the item in the owner's collection
      const itemRef = doc(db, 'users', item.ownerId, 'items', item.itemId);
      await updateDoc(itemRef, {
        destashStatus: newStatus,
        updatedAt: serverTimestamp(),
      });

      // 2. Update status in conversation and send system message
      await updateItemStatus(
        conversationId,
        currentUserId,
        item.itemId,
        item.name,
        currentStatus,
        newStatus
      );

    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update status. Please try again.');
    } finally {
      setIsChanging(false);
    }
  };

  const handleRemove = async () => {
    if (isRemoving) return;
    
    setIsRemoving(true);
    setShowRemoveConfirm(false);

    try {
      await removeItemFromConversation(
        conversationId,
        currentUserId,
        item.itemId,
        item.name
      );
    } catch (err) {
      console.error('Error removing item:', err);
      alert('Failed to remove item. Please try again.');
    } finally {
      setIsRemoving(false);
    }
  };

  const toggleDropdown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isChanging) {
      setShowDropdown(!showDropdown);
    }
  };

  return (
    <div className="glass-card-sm overflow-visible relative group" style={{ width: '140px' }}>
      {/* Remove button - shows on hover */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          setShowRemoveConfirm(true);
        }}
        disabled={isRemoving}
        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10 shadow-md"
        title="Remove from conversation"
      >
        {isRemoving ? (
          <Loader2 className="w-3 h-3 animate-spin" />
        ) : (
          <X className="icon-xs" />
        )}
      </button>

      {/* Remove confirmation overlay */}
      {showRemoveConfirm && (
        <div className="absolute inset-0 bg-slate-800/95 backdrop-blur-sm rounded-xl z-20 flex flex-col items-center justify-center p-2">
          <p className="text-xs text-glass-secondary text-center mb-2 font-body">
            Remove this item?
          </p>
          <div className="flex gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowRemoveConfirm(false);
              }}
              className="px-2 py-1 text-xs text-glass-secondary hover:bg-slate-700 rounded transition-default font-body"
            >
              Cancel
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleRemove();
              }}
              className="px-2 py-1 text-xs bg-red-500 text-white hover:bg-red-600 rounded transition-default font-body"
            >
              Remove
            </button>
          </div>
        </div>
      )}

      {/* Clickable Image */}
      <div 
        onClick={handleItemClick}
        className={`relative group/image ${onViewDetails ? 'cursor-pointer' : ''}`}
      >
        {item.photoUrl ? (
          <img 
            src={item.photoUrl} 
            alt={item.name}
            className="w-full h-20 object-cover rounded-t-lg"
          />
        ) : (
          <div className="w-full h-20 rainbow-gradient flex items-center justify-center rounded-t-lg">
            <AppIcons.Polish className="w-8 h-8 text-white" aria-label="Nail Polish" />
          </div>
        )}

        {/* Suggested by seller badge */}
        {item.suggestedBy === 'seller' && (
          <div className="absolute top-1 left-1">
            <span className="flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-bold rounded bg-amber-100 text-amber-700 border border-amber-300">
              <Sparkles className="w-2.5 h-2.5" />
              Suggested
            </span>
          </div>
        )}
        
        {/* View item overlay on hover */}
        {onViewDetails && (
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/image:opacity-100 transition-opacity rounded-t-lg flex items-center justify-center">
            <div className="flex items-center gap-1 text-white text-xs font-bold">
              <ExternalLink className="icon-xs" />
              View Item
            </div>
          </div>
        )}
      </div>
      
      {/* Info */}
      <div className="p-2">
        <p
          onClick={handleItemClick}
          className={`text-xs text-glass font-bold truncate font-heading ${onViewDetails ? 'cursor-pointer hover:underline' : ''}`}
        >
          {item.brand}
        </p>
        <p
          onClick={handleItemClick}
          className={`text-sm font-bold text-glass truncate mb-2 font-body ${onViewDetails ? 'cursor-pointer hover:underline' : ''}`}
        >
          {item.name}
        </p>
        
        {/* Status - Dropdown for owner, Badge for others */}
        {isOwner ? (
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={toggleDropdown}
              disabled={isChanging}
              className={`w-full flex items-center justify-between gap-1 px-2 py-1.5 rounded text-xs font-bold text-white font-body ${currentStatusInfo.color} hover:opacity-90 transition-opacity cursor-pointer`}
            >
              {isChanging ? (
                <Loader2 className="w-3 h-3 animate-spin mx-auto" />
              ) : (
                <>
                  <span className="flex items-center gap-1">
                    <currentStatusInfo.icon className="w-3 h-3 text-current fill-current" aria-label={currentStatusInfo.label} />
                    {currentStatusInfo.label}
                  </span>
                  <ChevronDown className={`w-3 h-3 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
                </>
              )}
            </button>
            
            {/* Dropdown */}
            {showDropdown && !isChanging && (
              <div
                className="glass-dropdown absolute left-0 right-0 mt-1 overflow-hidden"
                style={{
                  zIndex: 9999,
                  top: '100%'
                }}
              >
                {statusOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleStatusChange(option.value);
                    }}
                    className={`w-full text-left px-3 py-2 text-xs text-glass hover:bg-slate-700 flex items-center gap-2 transition-default font-body ${
                      option.value === currentStatus ? 'bg-slate-700 font-bold' : ''
                    }`}
                  >
                    <option.icon className={`w-3 h-3 ${option.iconColor} fill-current`} aria-label={option.label} />
                    <span>{option.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div
            className={`px-2 py-1.5 rounded text-xs font-bold text-white text-center font-body ${currentStatusInfo.color} flex items-center justify-center gap-1`}
          >
            <currentStatusInfo.icon className="w-3 h-3 text-current fill-current" aria-label={currentStatusInfo.label} />
            {currentStatusInfo.label}
          </div>
        )}
      </div>
    </div>
  );
};

export default PinnedItemWithStatus;
